import {
  ConeGeometry,
  Group,
  MathUtils,
  Mesh,
  MeshBasicMaterial,
  Plane,
  Raycaster,
  SphereGeometry,
  TorusGeometry,
  Vector2,
  Vector3
} from 'three';
import * as TWEEN from '@tweenjs/tween.js';
import { Tween } from '@tweenjs/tween.js';
import { EngineContext } from './engine-context';

/**
 * Manages the orbit pivot marker, temporary pivot display during rotation,
 * and Alt+Click pivot placement. Extracted from Engine to reduce class size.
 */
export class OrbitPivotManager {
  private marker?: Group;
  private markerPulse = 1;
  private markerTween?: Tween<{ t: number }>;
  private active = false;

  /** Called when a pivot is set or cleared. */
  onActiveChange: ((active: boolean) => void) | null = null;
  private showingDuringRotation = false;
  private pivotTween?: Tween<{ t: number }>;
  private defaultTarget = new Vector3();

  // Raycaster state for Alt+Click placement
  private raycaster = new Raycaster();
  private pointer = new Vector2();
  private plane = new Plane();
  private planeNormal = new Vector3();
  private point = new Vector3();

  // Bound handlers (for removal)
  private pointerDownHandler?: (event: PointerEvent) => void;
  readonly controlsActionStartHandler: (event: { action?: string }) => void;
  readonly controlsActionEndHandler: (event: { action?: string }) => void;

  constructor(private ctx: EngineContext) {
    this.controlsActionStartHandler = (event) => {
      if (event.action === 'rotate') {
        this.showTemporary();
        this.ctx.canvas.style.cursor = 'move';
      } else if (event.action === 'pan') {
        this.ctx.canvas.style.cursor = 'grabbing';
      } else if (event.action === 'zoom') {
        this.ctx.canvas.style.cursor = 'ns-resize';
      }
    };

    this.controlsActionEndHandler = (event) => {
      if (event.action === 'rotate' && this.showingDuringRotation) {
        this.hideTemporary();
      }
      this.ctx.canvas.style.cursor = 'default';
    };
  }

  /**
   * Initialize marker, hint, pointer handler. Call after engine init.
   * Pivot points and the marker are in the camera's (view-frame) space, like
   * the controls' target.
   */
  init(objectSize: Vector3, objectCenter: Vector3): void {
    this.defaultTarget.copy(objectCenter);
    this.ensureMarker(objectSize);

    this.pointerDownHandler = (event: PointerEvent) => {
      if (!event.altKey || event.button !== 0) return;
      event.preventDefault();
      event.stopPropagation();

      if (event.shiftKey) {
        this.reset();
        return;
      }

      const pt = this.getPointFromEvent(event);
      if (!pt) return;
      this.set(pt, true);
    };
    this.ctx.canvas.addEventListener('pointerdown', this.pointerDownHandler, true);
  }

  /** Called from Engine.animate() to keep marker scale consistent with zoom */
  update(): void {
    if (this.marker?.visible) {
      const scale = MathUtils.clamp(1 / this.ctx.camera.zoom, 0.6, 3);
      this.marker.scale.setScalar(scale * this.markerPulse);
    }
  }

  /**
   * Show temporary pivot at the rotation center (for view cube and controls
   * rotation), or at `at` when the rotation center is elsewhere (3D mouse).
   */
  showTemporary(at?: Vector3): void {
    const controls = this.ctx.controls as Record<string, unknown>;
    const hasExplicit = typeof controls['hasDecoupledPivot'] === 'function'
      ? (controls['hasDecoupledPivot'] as () => boolean)()
      : false;

    if (!hasExplicit && this.marker) {
      this.showingDuringRotation = true;
      const target = at ?? this.ctx.controls.target;
      if (target) {
        this.marker.visible = true;
        this.marker.position.copy(target);
        this.pulseMarker();
      }
    }
  }

  /** Hide the temporary pivot (only if it was a temporary one, not explicitly set) */
  hideTemporary(): void {
    if (this.showingDuringRotation) {
      this.showingDuringRotation = false;
      if (this.marker) {
        this.markerTween?.stop();
        this.markerTween = undefined;
        this.markerPulse = 1;
        this.marker.visible = false;
      }
    }
  }

  /** Clear decoupled pivot — rotation goes back to normal (around target) */
  clear(): void {
    this.pivotTween?.stop();
    this.pivotTween = undefined;
    this.markerTween?.stop();
    this.markerTween = undefined;
    this.markerPulse = 1;

    const controls = this.ctx.controls as Record<string, unknown>;
    if (typeof controls['clearDecoupledPivot'] === 'function') {
      (controls['clearDecoupledPivot'] as () => void)();
    }

    this.updateMarker(null);
  }

  /** Reset orbit pivot (alias for clear) */
  reset(): void {
    this.clear();
  }

  /** Whether a pivot is set. */
  get isActive(): boolean {
    return this.active;
  }

  /** Dispose marker, hint, and event listeners */
  dispose(): void {
    if (this.pointerDownHandler) {
      this.ctx.canvas.removeEventListener('pointerdown', this.pointerDownHandler, true);
      this.pointerDownHandler = undefined;
    }
    this.pivotTween?.stop();
    this.markerTween?.stop();
    this.onActiveChange = null;
  }

  // ── Private helpers ─────────────────────────────────────────────────

  private set(point: Vector3, showMarker: boolean): void {
    this.pivotTween?.stop();
    this.pivotTween = undefined;

    const controls = this.ctx.controls as Record<string, unknown>;
    if (typeof controls['setDecoupledPivot'] === 'function') {
      (controls['setDecoupledPivot'] as (p: Vector3) => void)(point);
    }

    this.updateMarker(showMarker ? point : null);
  }

  private getPointFromEvent(event: PointerEvent): Vector3 | null {
    const rect = this.ctx.canvas.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return null;

    this.pointer.set(
      ((event.clientX - rect.left) / rect.width) * 2 - 1,
      -((event.clientY - rect.top) / rect.height) * 2 + 1
    );

    this.raycaster.setFromCamera(this.pointer, this.ctx.camera);

    const pickRoots = [
      this.ctx.group,
      this.ctx.optimizedGroup,
      this.ctx.optimizedGroupParametric,
      this.ctx.infoGroup
    ].filter((obj) => obj && obj.visible);
    this.raycaster.firstHitOnly = true;
    const intersects = this.raycaster.intersectObjects(pickRoots, true);
    if (intersects.length > 0) {
      return this.point.copy(this.ctx.toViewSpace(intersects[0].point));
    }

    this.ctx.camera.getWorldDirection(this.planeNormal);
    this.plane.setFromNormalAndCoplanarPoint(this.planeNormal, this.ctx.toWorldSpace(this.ctx.controls.target));

    if (this.raycaster.ray.intersectPlane(this.plane, this.point)) {
      return this.point.copy(this.ctx.toViewSpace(this.point));
    }

    return null;
  }

  private ensureMarker(size: Vector3): void {
    if (this.marker) return;

    const maxSize = Math.max(size.x, size.y, size.z);
    const radius = Math.min(Math.max(maxSize * 0.025, 0.4), 15);

    const heroGreen = 0xa0ff00;
    const heroGreenDark = 0x7acc00;
    const white = 0xffffff;

    const marker = new Group();

    // Central sphere
    marker.add(new Mesh(
      new SphereGeometry(radius * 0.2, 24, 24),
      new MeshBasicMaterial({ color: heroGreen, transparent: true, opacity: 0.95, depthTest: false, depthWrite: false })
    ));

    // Outer glow sphere
    marker.add(new Mesh(
      new SphereGeometry(radius * 0.32, 16, 16),
      new MeshBasicMaterial({ color: heroGreen, transparent: true, opacity: 0.2, depthTest: false, depthWrite: false })
    ));

    // Axis rings (tori)
    const torusRadius = radius * 0.8;
    const tubeRadius = radius * 0.06;
    const torusSegs = 64;
    const tubeSegs = 16;

    const xRing = new Mesh(
      new TorusGeometry(torusRadius, tubeRadius, tubeSegs, torusSegs),
      new MeshBasicMaterial({ color: heroGreen, transparent: true, opacity: 0.8, depthTest: false, depthWrite: false })
    );
    xRing.rotation.y = Math.PI / 2;
    marker.add(xRing);

    const yRing = new Mesh(
      new TorusGeometry(torusRadius, tubeRadius, tubeSegs, torusSegs),
      new MeshBasicMaterial({ color: heroGreenDark, transparent: true, opacity: 0.8, depthTest: false, depthWrite: false })
    );
    yRing.rotation.x = Math.PI / 2;
    marker.add(yRing);

    const zRing = new Mesh(
      new TorusGeometry(torusRadius, tubeRadius, tubeSegs, torusSegs),
      new MeshBasicMaterial({ color: white, transparent: true, opacity: 0.6, depthTest: false, depthWrite: false })
    );
    marker.add(zRing);

    // Axis cones
    const coneHeight = radius * 0.22;
    const coneRadius = radius * 0.08;

    const makeCone = (color: number, opacity: number, pos: [number, number, number], rot?: [number, number, number], scale = 1): Mesh => {
      const cone = new Mesh(
        new ConeGeometry(coneRadius * scale, coneHeight * scale, 12),
        new MeshBasicMaterial({ color, transparent: true, opacity, depthTest: false, depthWrite: false })
      );
      cone.position.set(...pos);
      if (rot) cone.rotation.set(...rot);
      return cone;
    };

    marker.add(makeCone(heroGreen, 0.9, [torusRadius + coneHeight * 0.5, 0, 0], [0, 0, -Math.PI / 2]));
    marker.add(makeCone(heroGreen, 0.5, [-torusRadius - coneHeight * 0.35, 0, 0], [0, 0, Math.PI / 2], 0.7));
    marker.add(makeCone(heroGreenDark, 0.9, [0, torusRadius + coneHeight * 0.5, 0]));
    marker.add(makeCone(heroGreenDark, 0.5, [0, -torusRadius - coneHeight * 0.35, 0], [Math.PI, 0, 0], 0.7));
    marker.add(makeCone(white, 0.9, [0, 0, torusRadius + coneHeight * 0.5], [Math.PI / 2, 0, 0]));
    marker.add(makeCone(white, 0.5, [0, 0, -torusRadius - coneHeight * 0.35], [-Math.PI / 2, 0, 0], 0.7));

    // Finalize marker
    marker.name = 'orbitPivotMarker';
    marker.renderOrder = 999;
    marker.visible = false;
    marker.frustumCulled = false;
    marker.traverse((child) => {
      child.renderOrder = 999;
      child.frustumCulled = false;
    });

    this.marker = marker;
    this.ctx.viewFrame.add(marker);
  }

  private updateMarker(point: Vector3 | null): void {
    const pivotActive = !!point;
    const changed = pivotActive !== this.active;
    this.active = pivotActive;
    if (changed) this.onActiveChange?.(pivotActive);

    const controls = this.ctx.controls as Record<string, unknown>;
    if (controls) {
      controls['zoomToCursor'] = !pivotActive;
    }

    if (!this.marker) return;

    if (!point) {
      this.markerTween?.stop();
      this.markerTween = undefined;
      this.markerPulse = 1;
      this.marker.visible = false;
      return;
    }

    this.marker.visible = true;
    this.marker.position.copy(point);
    this.pulseMarker();
  }

  private pulseMarker(): void {
    if (!this.marker) return;
    this.markerTween?.stop();
    this.markerPulse = 0.7;
    const pulse = { t: this.markerPulse };
    this.markerTween = new TWEEN.Tween(pulse)
      .to({ t: 1 }, 140)
      .easing(TWEEN.Easing.Quadratic.Out)
      .onUpdate(() => {
        this.markerPulse = pulse.t;
      })
      .onComplete(() => {
        this.markerTween = undefined;
      })
      .start();
  }
}
